/**
 * FavoritesRail — reusable pill rail for free-text field favorites.
 *
 * Mirrors the design from the wizard mockups (Awards/Exhibitions/Publications/
 * Additional Info): a row of saved values shown as Hi-Fi pills above an input,
 * with an optional star button next to the input that toggles the current
 * value as a saved favorite. Click a pill to apply it to the input; click its
 * X to remove from favorites.
 *
 * Visually matches the GettyFavoritesStrip style ("⭐ FAVORITES" caps label
 * + light pills) so favorites look identical across Getty Vocab, Exhibitions,
 * Publications, Awards & Honors, and Current Location.
 */

import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { FieldFavoriteType } from '../utils/fieldFavoritesApi';
import { useFieldFavorites } from '../hooks/useFieldFavorites';
import { FavoritesPillStrip, FavoritePill } from './edit-object/FavoritesPillStrip';

interface FavoritesRailProps {
  type: FieldFavoriteType;
  currentValue: string;
  onPick: (value: string) => void;
  emptyHint?: string;
  /** Hide the star button (for shared section-level rails where rows save themselves). */
  hideStar?: boolean;
}

const DEFAULT_EMPTY_HINT: Record<FieldFavoriteType, string> = {
  awards: 'No favorite awards saved yet. Click the star to save the current value.',
  exhibitions: 'No favorite exhibitions saved yet. Click the star to save the current value.',
  publications: 'No favorite publications saved yet. Click the star to save the current value.',
  locations: 'No favorite locations saved yet. Click the star to save the current value.',
  provenance: 'No favorite provenance entries saved yet. Click the star to save the current value.',
};

export const FavoritesRail: React.FC<FavoritesRailProps> = ({ type, currentValue, onPick, emptyHint, hideStar }) => {
  const ctx = useFieldFavorites();
  // Render nothing when no provider is mounted (e.g. isolated component tests).
  if (!ctx) return null;
  const { getFavoritesForType, isFavorite, addFavorite, removeFavorite } = ctx;
  const favorites = getFavoritesForType(type);
  const trimmed = currentValue.trim();
  const currentSaved = trimmed.length > 0 && isFavorite(type, trimmed);

  const handleStarClick = () => {
    if (!trimmed) return;
    if (currentSaved) {
      void removeFavorite(type, trimmed);
    } else {
      void addFavorite(type, trimmed);
    }
  };

  const pills: FavoritePill[] = favorites.map((value) => ({
    id: value,
    label: value,
    active: trimmed.length > 0 && value.toLowerCase() === trimmed.toLowerCase(),
  }));

  const handlePillClick = (pill: FavoritePill) => onPick(pill.label);
  const handlePillRemove = (pill: FavoritePill) => void removeFavorite(type, pill.label);

  return (
    <Box data-testid={`favorites-rail-${type}`}>
      {!hideStar && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Tooltip title={currentSaved ? 'Unsave from favorites' : 'Save current as favorite'}>
            <span>
              <IconButton
                size="small"
                onClick={handleStarClick}
                disabled={!trimmed}
                color={currentSaved ? 'warning' : 'default'}
                aria-label={currentSaved ? 'Unsave from favorites' : 'Save current as favorite'}
              >
                {currentSaved ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      <FavoritesPillStrip
        title="⭐ FAVORITES"
        pills={pills}
        onPillClick={handlePillClick}
        onPillRemove={handlePillRemove}
        emptyHint={emptyHint ?? DEFAULT_EMPTY_HINT[type]}
      />
    </Box>
  );
};
